import DraftIcon from '@/components/icons/draft';
import { UserContext } from '@/contexts/userContext';
import { FolderPathItem, VaultContext } from '@/contexts/vaultContext';
import { useAuthIsLoaded } from '@/hooks/useAuthIsLoaded';
import { useLocalization } from '@/hooks/useLocalization';
import {
  DocumentRecord,
  useDownloadAccountDocumentMutation,
  useDownloadPersonalDocumentMutation,
  useGetDocumentsByAccountIdQuery,
  useGetDocumentsQuery,
  usePreviewAccountDocumentMutation,
  usePreviewPersonalDocumentMutation,
  useGetDocumentTagsQuery,
} from '@/store/documentsApi';
import FolderIcon from '@mui/icons-material/Folder';
import {
  Box,
  CircularProgress,
  Skeleton,
  Stack,
  Typography,
} from '@mui/material';
import { GridColDef, useGridApiRef } from '@mui/x-data-grid-premium';
import dayjs from '@/config/dayjs';
import { useContext, useEffect, useMemo } from 'react';
import { DocVaultGrid } from './DocVaultGrid';
import { VaultEmptyOverlay, VaultListState } from './VaultListState';
import { getQueriesToRetry, getVaultListStatus } from './vaultListStatus';
import {
  getDocumentDownloadName,
  getDocumentFilenameForBlobHandling,
  normalizeDocumentRecords,
  skeletonRows,
  sortPersonalStorageRows,
} from './documentVaultGridUtils';

const FOLDER_LAST_UPDATED_PLACEHOLDER = '\u2014';

const formatUploadDate = (value: string) =>
  dayjs(value).isValid() ? dayjs(value).format('MM/DD/YYYY') : 'Invalid Date';

const NoDocumentsOverlay = () => {
  const { t } = useLocalization();
  return (
    <Stack
      height='100%'
      alignItems='center'
      justifyContent='center'
    >
      <Typography variant='h6'>
        {t('documentVault.noDocumentsFound')}
      </Typography>
    </Stack>
  );
};

const gridSlots = { noRowsOverlay: NoDocumentsOverlay };
const paginatedGridSlots = {
  noRowsOverlay: VaultEmptyOverlay,
  noResultsOverlay: VaultEmptyOverlay,
};

const getColumns = (showFolderDash: boolean): GridColDef<DocumentRecord>[] => [
  {
    field: 'Name',
    headerName: 'File',
    flex: 1,
    renderCell: ({ row }) => (
      <Stack
        flexDirection='row'
        alignItems='center'
        gap={1}
      >
        {row.IsFolder ? <FolderIcon /> : <DraftIcon />} {row.Name}
      </Stack>
    ),
    filterable: true,
  },
  {
    field: 'UploadDate',
    headerName: 'Last Updated',
    width: 144,
    type: 'date',
    valueFormatter: (value, row) =>
      showFolderDash && row.IsFolder
        ? FOLDER_LAST_UPDATED_PLACEHOLDER
        : formatUploadDate(value),
    filterable: false,
  },
  {
    field: 'AddedByName',
    headerName: 'Uploaded By',
    width: 144,
    filterable: true,
  },
];

interface PersonalStorageGridProps {
  searchTerm?: string;
  wealthAccountId?: number;
  folderId?: string;
  investorId?: number;
  paginated?: boolean;
}

const PersonalStorageGrid = ({
  searchTerm,
  wealthAccountId,
  folderId,
  investorId,
  paginated = false,
}: PersonalStorageGridProps) => {
  const { currentRole } = useContext(UserContext);
  const isAdvisor = (currentRole || '').toLowerCase() === 'advisor';
  const isAuthLoaded = useAuthIsLoaded();
  const { t } = useLocalization();
  const { folderPath, setFolderPath } = useContext(VaultContext);
  const isAccountView = wealthAccountId !== undefined;
  const isAdvisorList = isAdvisor && !isAccountView;
  const columns = useMemo(() => getColumns(isAdvisorList), [isAdvisorList]);
  const bulkDownloadContextName = useMemo(() => {
    if (folderPath.length > 0) {
      return folderPath[folderPath.length - 1].name;
    }
    return 'document-vault';
  }, [folderPath]);

  const apiRef = useGridApiRef();

  const isGeneralQuerySkipped =
    !isAuthLoaded || isAccountView || (isAdvisor && !investorId);

  // General-purpose document fetch
  const {
    currentData: generalDocumentsData,
    isLoading: isGeneralLoading,
    isFetching: isGeneralFetching,
    isUninitialized: isGeneralUninitialized,
    error: generalError,
    refetch: refetchGeneral,
  } = useGetDocumentsQuery(
    {
      SearchTerm: searchTerm,
      folderId: folderId ? parseInt(folderId, 10) : undefined,
      sort: [{ property: 'UploadDate', direction: 'desc', order: 0 }],
      InvestorId: investorId,
    },
    {
      skip: isGeneralQuerySkipped,
    },
  );

  // Account-specific document fetch
  const {
    currentData: accountDocumentsData,
    isLoading: isAccountLoading,
    isFetching: isAccountFetching,
    error: accountError,
  } = useGetDocumentsByAccountIdQuery(
    {
      accountId: wealthAccountId ?? 0,
      SearchTerm: searchTerm,
      sort: [{ property: 'UploadDate', direction: 'desc', order: 0 }],
      InvestorId: investorId,
    },
    {
      skip: !isAuthLoaded || !isAccountView || (isAdvisor && !investorId),
    },
  );

  // Determine which data and loading state to use
  const hasDocumentsData = Boolean(
    isAccountView ? accountDocumentsData : generalDocumentsData,
  );
  const isLoading = isAccountView ? isAccountLoading : isGeneralLoading;
  const isFetching = isAccountView ? isAccountFetching : isGeneralFetching;
  const hasError = Boolean(isAccountView ? accountError : generalError);

  const generalQuery = {
    isLoading: isGeneralLoading,
    isFetching: isGeneralFetching,
    isError: Boolean(generalError),
    isUninitialized: isGeneralUninitialized,
    hasData: Boolean(generalDocumentsData),
    skip: isGeneralQuerySkipped,
  };
  const vaultListStatus = getVaultListStatus([generalQuery]);
  const handleRetry = () => {
    if (getQueriesToRetry([generalQuery]).length > 0) {
      void refetchGeneral();
    }
  };

  const rows: DocumentRecord[] = useMemo(() => {
    const normalized = normalizeDocumentRecords(
      isAccountView ? accountDocumentsData : generalDocumentsData,
    );
    return isAdvisorList ? sortPersonalStorageRows(normalized) : normalized;
  }, [
    isAccountView,
    isAdvisorList,
    accountDocumentsData,
    generalDocumentsData,
  ]);

  // Advisor-only: fetch document tags for selected investor (parity with legacy)
  useGetDocumentTagsQuery(
    typeof investorId === 'number' ? { InvestorId: investorId } : undefined,
    {
      skip: !isAuthLoaded || !isAdvisor || typeof investorId !== 'number',
    },
  );

  const [previewPersonalDoc, { isLoading: isPreviewingPersonal }] =
    usePreviewPersonalDocumentMutation();
  const [downloadPersonalDoc, { isLoading: isDownloadingPersonal }] =
    useDownloadPersonalDocumentMutation();
  const [previewAccountDoc, { isLoading: isPreviewingAccount }] =
    usePreviewAccountDocumentMutation();
  const [downloadAccountDoc, { isLoading: isDownloadingAccount }] =
    useDownloadAccountDocumentMutation();

  // The vault list rethrows the failure, so DocVaultGrid shows Retry. The account view only logs it.
  const rethrowUnlessAccountView = (error: unknown) => {
    if (!isAccountView) {
      throw error;
    }
    console.error('Document action failed:', error);
  };

  const handleRowAction = async (
    action: 'preview' | 'download' | 'folderClick',
    file: DocumentRecord,
  ) => {
    if (action === 'folderClick' && !isAccountView) {
      if (
        folderPath.length === 0 ||
        folderPath[folderPath.length - 1].id !== file.VaultFileId
      ) {
        const newPath: FolderPathItem[] = [
          ...folderPath,
          { id: file.VaultFileId, name: file.Name },
        ];
        setFolderPath(newPath);
      }
      return;
    }

    // Trust the action from the child component, no need to re-evaluate if previewable
    if (wealthAccountId) {
      // --- ACCOUNT-SPECIFIC LOGIC ---
      if (action === 'preview') {
        try {
          // Just call the mutation for its side effect. It now handles the preview logic.
          await previewAccountDoc({
            wealthAccountId,
            vaultId: file.VaultFileId,
            fileName: getDocumentFilenameForBlobHandling(file),
            InvestorId: investorId,
          }).unwrap();
        } catch (error) {
          rethrowUnlessAccountView(error);
        }
      } else if (action === 'download') {
        try {
          await downloadAccountDoc({
            wealthAccountId,
            vaultId: file.VaultFileId,
            filename: getDocumentDownloadName(file),
            InvestorId: investorId,
          }).unwrap();
        } catch (error) {
          rethrowUnlessAccountView(error);
        }
      }
    } else {
      // --- GENERAL DOCUMENT VAULT LOGIC ---
      if (action === 'preview') {
        try {
          // Just call the mutation for its side effect. It now handles the preview logic.
          await previewPersonalDoc({
            vaultId: file.VaultFileId,
            InvestorId: investorId,
            fileName: getDocumentFilenameForBlobHandling(file),
          }).unwrap();
        } catch (error) {
          rethrowUnlessAccountView(error);
        }
      } else if (action === 'download') {
        try {
          await downloadPersonalDoc({
            vaultId: file.VaultFileId,
            filename: getDocumentDownloadName(file),
            InvestorId: investorId,
          }).unwrap();
        } catch (error) {
          rethrowUnlessAccountView(error);
        }
      }
    }
  };

  useEffect(() => {
    const gridApi = apiRef.current;
    if (!gridApi) {
      return;
    }

    if (searchTerm) {
      gridApi.setFilterModel({
        items: [
          {
            field: 'Name',
            operator: 'contains',
            value: searchTerm,
          },
        ],
      });
    } else {
      gridApi.setFilterModel({ items: [] });
    }
  }, [searchTerm, apiRef]);

  const isPaginatedList = paginated && !isAccountView;

  if (!isPaginatedList && (isLoading || (isFetching && !hasDocumentsData))) {
    if (!isAccountView) {
      return <Typography px={1}>{t('loading')}</Typography>;
    }
    return (
      <Stack
        gap={1}
        px={1}
      >
        {currentRole === 'advisor' && (
          <Skeleton
            variant='rectangular'
            height={40}
            sx={{ mb: 2 }}
          />
        )}
        {skeletonRows.map((index) => (
          <Skeleton
            key={index}
            variant='rectangular'
            height={52}
          />
        ))}
      </Stack>
    );
  }

  if (!isPaginatedList && hasError) {
    console.error(
      'Error loading personal storage:',
      isAccountView ? accountError : generalError,
    );
    return (
      <Typography color='error'>{t('documentVault.loadError')}</Typography>
    );
  }

  const paginationResetKey = `${String(investorId ?? '')}|${folderId ?? ''}|${searchTerm ?? ''}`;

  const list = (
    <>
      <Box>
        <Box>
          {isFetching && searchTerm && (
            <Stack
              direction='row'
              spacing={1}
              alignItems='center'
              justifyContent='center'
              sx={{ mb: 1 }}
            >
              <CircularProgress size={16} />
              <Typography variant='caption'>Loading Search...</Typography>
            </Stack>
          )}

          <DocVaultGrid<DocumentRecord>
            columns={columns}
            rows={rows}
            loading={
              isFetching ||
              isPreviewingPersonal ||
              isDownloadingPersonal ||
              isPreviewingAccount ||
              isDownloadingAccount
            }
            checkboxSelection
            disableRowSelectionOnClick
            enableXsListView
            rowHeight={52}
            apiRef={apiRef}
            onRowAction={handleRowAction}
            investorId={investorId}
            currentFolderId={folderId ? parseInt(folderId, 10) : undefined}
            bulkDownloadContextName={bulkDownloadContextName}
            showBulkActions //To enable Delete and Move options
            enableAdvisorRowActions={isAdvisorList}
            disableColumnSorting={isAdvisorList}
            slots={
              isAccountView
                ? undefined
                : isPaginatedList
                  ? paginatedGridSlots
                  : gridSlots
            }
            paginated={isPaginatedList}
            paginationResetKey={
              isPaginatedList ? paginationResetKey : undefined
            }
          />
        </Box>
        {!isPaginatedList && (
          <Box
            sx={{
              mt: 2,
              fontWeight: 600,
              display: 'flex',
              justifyContent: 'space-between',
              flexShrink: 0,
            }}
          >
            <Typography variant='subtitle1'>Total Items</Typography>
            <Typography variant='subtitle1'>{rows.length}</Typography>
          </Box>
        )}
      </Box>
    </>
  );

  if (isAccountView) {
    return list;
  }

  if (!paginated) {
    return list;
  }

  return (
    <VaultListState
      status={vaultListStatus}
      onRetry={handleRetry}
    >
      {list}
    </VaultListState>
  );
};

export default PersonalStorageGrid;
